import { alertAdded } from 'features/alerts/redux/alerts-slice';
import { useResetPropertyMyViewSettingsMutation } from 'features/my-view/gql/_gen_/my-metrics.gql';
import { SettingsScope } from 'graphql/gql-types';
import {
	usePropertySettings,
	useUniversalSettings,
} from 'hooks/useUserSettings';
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch } from 'redux/hooks';
import { useUser } from '../../../features/users/context/userContext';
import {
	getMyViewSettingsScopeStorageKey,
	getMyViewSettingsTransition,
	resolveMyViewSettingsScope,
} from '../my-view-settings-helpers';

interface UseMyViewSettingsScopeArgs {
	propertyId?: string | null;
}

export function useMyViewSettingsScope({
	propertyId,
}: UseMyViewSettingsScopeArgs) {
	const dispatch = useAppDispatch();
	const { user } = useUser();
	const settingsScopeStorageKey = getMyViewSettingsScopeStorageKey(propertyId);
	const storedScopeFromLocalStorage = propertyId
		? (localStorage.getItem(settingsScopeStorageKey) as SettingsScope | null)
		: null;
	const universalPrefs = useUniversalSettings();
	const propertyPrefs = usePropertySettings();
	const { isReady: isUniversalPrefsReady, getSetting: getUniversalSetting } =
		universalPrefs;
	const {
		isReady: isPropertyPrefsReady,
		getSetting: getPropertySetting,
		setSetting: setPropertySetting,
		refetchSettings: refetchPropertySettings,
	} = propertyPrefs;
	const [settingsScope, setSettingsScope] = useState<SettingsScope>(
		SettingsScope.universal,
	);
	const [, setHasResolvedSettingsScope] = useState(
		() => !!storedScopeFromLocalStorage,
	);
	const [isUpdatingSettingsScope, setIsUpdatingSettingsScope] = useState(false);
	const [isPendingUniversalSwitch, setIsPendingUniversalSwitch] =
		useState(false);

	const [resetPropertyMyViewSettings] =
		useResetPropertyMyViewSettingsMutation();

	useEffect(() => {
		if (!propertyId) {
			setSettingsScope(SettingsScope.property);
			setHasResolvedSettingsScope(true);
			return;
		}

		if (!isUniversalPrefsReady || !isPropertyPrefsReady) {
			setHasResolvedSettingsScope(true);
			return;
		}

		// Read localStorage inside the effect (not as a dep) so that clearing it
		// during handleConfirmUniversalSwitch does not immediately re-trigger scope
		// resolution with stale Apollo cache data. The effect will only re-run when
		// Apollo data actually changes (after refetch completes), at which point both
		// savedScope and propertyColumnSettings will be null and the resolved scope
		// will correctly be SettingsScope.universal.
		const currentStoredScope = localStorage.getItem(
			settingsScopeStorageKey,
		) as SettingsScope | null;

		const savedScope = getPropertySetting(
			'settingsScope',
			null,
		) as SettingsScope | null;
		const propertyColumnSettings = getPropertySetting('myViewColumns', null);
		const universalColumnSettings = getUniversalSetting('myViewColumns', null);
		const resolvedScope = resolveMyViewSettingsScope({
			savedScope,
			storedScopeFromLocalStorage: currentStoredScope,
			propertyColumnSettings,
			universalColumnSettings,
		});

		setSettingsScope(resolvedScope);
		setHasResolvedSettingsScope(true);
	}, [
		propertyId,
		isUniversalPrefsReady,
		isPropertyPrefsReady,
		getPropertySetting,
		getUniversalSetting,
		settingsScopeStorageKey,
	]);

	const handleScopeToggle = (isUniversal: boolean) => {
		if (!propertyId || isUpdatingSettingsScope) return;

		if (isUniversal && settingsScope === SettingsScope.property) {
			// Defer opening the confirmation modal by one event-loop tick so the
			// originating pointerdown event finishes propagating before the dialog
			// mounts. Without this the dialog's onPointerDownOutside handler fires
			// on the same event that opened it, closing the modal immediately.
			setTimeout(() => setIsPendingUniversalSwitch(true), 0);
			return;
		}

		void handlePropertyToggle();
	};

	const handlePropertyToggle = async () => {
		if (!propertyId || isUpdatingSettingsScope) return;

		const previousScope = settingsScope;
		const universalColumnSettings = getUniversalSetting('myViewColumns', null);
		const propertyColumnSettings = getPropertySetting('myViewColumns', null);
		const { shouldCopyUniversalToProperty } = getMyViewSettingsTransition({
			currentScope: settingsScope,
			nextScope: SettingsScope.property,
			universalColumnSettings,
			propertyColumnSettings,
		});

		setSettingsScope(SettingsScope.property);
		setIsUpdatingSettingsScope(true);

		try {
			if (shouldCopyUniversalToProperty) {
				await setPropertySetting('myViewColumns', universalColumnSettings);
			}

			await setPropertySetting('settingsScope', SettingsScope.property);
			localStorage.setItem(settingsScopeStorageKey, SettingsScope.property);
			setHasResolvedSettingsScope(true);
		} catch (error) {
			setSettingsScope(previousScope);
			console.error('Failed to persist MyView settings scope', error);
			dispatch(
				alertAdded(
					'error',
					"Could not save this property's MyView settings preference. Please try again.",
				),
			);
			setHasResolvedSettingsScope(true);
		} finally {
			setIsUpdatingSettingsScope(false);
		}
	};

	const handleConfirmUniversalSwitch = useCallback(async () => {
		if (!propertyId || !user?.id) {
			setIsPendingUniversalSwitch(false);
			return;
		}

		setIsPendingUniversalSwitch(false);
		setIsUpdatingSettingsScope(true);
		const previousScope = settingsScope;

		try {
			await resetPropertyMyViewSettings({
				variables: { userId: String(user.id), propertyId },
			});

			localStorage.removeItem(settingsScopeStorageKey);
			refetchPropertySettings();
			setSettingsScope(SettingsScope.universal);
			setHasResolvedSettingsScope(true);
		} catch (error) {
			setSettingsScope(previousScope);
			console.error('Failed to reset property MyView settings', error);
			dispatch(
				alertAdded(
					'error',
					'Could not switch to Universal settings for this property. Please try again.',
				),
			);
			setHasResolvedSettingsScope(true);
		} finally {
			setIsUpdatingSettingsScope(false);
		}
	}, [
		propertyId,
		user?.id,
		settingsScope,
		settingsScopeStorageKey,
		resetPropertyMyViewSettings,
		refetchPropertySettings,
		dispatch,
	]);

	const handleCancelUniversalSwitch = useCallback(() => {
		setIsPendingUniversalSwitch(false);
	}, []);

	return {
		settingsScope,
		isUpdatingSettingsScope,
		isPendingUniversalSwitch,
		handleScopeToggle,
		handleConfirmUniversalSwitch,
		handleCancelUniversalSwitch,
	};
}
